import React, { useState } from 'react';
import { XIcon, SearchIcon, PlusIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const suggestedKeywords = ['AI', 'Electric Vehicles', 'Batteries', 'Semiconductors', 'Biotech', 'Beijing', 'Shanghai', 'Shenzhen', 'Autonomous Driving', 'Robotics', 'Fintech', 'Solar', 'LLM', 'Drones', 'Genomics'];
type KeywordSearchProps = {
  keywords: string[];
  onKeywordsChange: (keywords: string[]) => void;
  onSearch: () => void;
};
export function KeywordSearch({
  keywords,
  onKeywordsChange,
  onSearch
}: KeywordSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const addKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onKeywordsChange([...keywords, trimmed]);
    }
    setInputValue('');
  };
  const addKeywordAndSearch = (keyword: string) => {
    const trimmed = keyword.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onKeywordsChange([...keywords, trimmed]);
    }
    setInputValue('');
    // Auto-trigger search when clicking a suggestion
    setTimeout(() => onSearch(), 0);
  };
  const removeKeyword = (keyword: string) => {
    onKeywordsChange(keywords.filter(k => k !== keyword));
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addKeyword(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && keywords.length > 0) {
      removeKeyword(keywords[keywords.length - 1]);
    }
  };
  const availableSuggestions = suggestedKeywords.filter(s => !keywords.includes(s));
  return <div className="space-y-4" data-id="element-188">
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 backdrop-blur-sm" data-id="element-189">
        <div className="flex flex-wrap gap-2 mb-3" data-id="element-190">
          <AnimatePresence mode="popLayout" data-id="element-191">
            {keywords.map(keyword => <motion.span key={keyword} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} layout className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium border border-amber-500/30" data-id="element-192">
                {keyword}
                <button onClick={() => removeKeyword(keyword)} className="hover:bg-amber-500/30 rounded-full p-0.5 transition-colors" aria-label={`Remove ${keyword}`} data-id="element-193">
                  <XIcon className="w-3.5 h-3.5" data-id="element-194" />
                </button>
              </motion.span>)}
          </AnimatePresence>
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={keywords.length === 0 ? 'Add keywords to narrow your search...' : 'Add more...'} className="flex-1 min-w-[200px] bg-transparent text-white placeholder-slate-500 outline-none text-sm" data-id="element-195" />
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-700" data-id="element-196">
          <p className="text-xs text-slate-500" data-id="element-197">
            Press Enter to add • Keywords combine with AND logic
          </p>
          <button onClick={onSearch} disabled={keywords.length === 0} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg transition-colors disabled:cursor-not-allowed" data-id="element-198">
            <SearchIcon className="w-4 h-4" data-id="element-199" />
            Search
          </button>
        </div>
      </div>

      <div className="space-y-2" data-id="element-200">
        <p className="text-xs text-slate-500 uppercase tracking-wider font-medium" data-id="element-201">
          Suggested Keywords
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-202">
          {availableSuggestions.slice(0, 10).map(suggestion => <button key={suggestion} onClick={() => addKeywordAndSearch(suggestion)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white rounded-full text-sm border border-slate-700 hover:border-slate-600 transition-colors" data-id="element-203">
              <PlusIcon className="w-3 h-3" data-id="element-204" />
              {suggestion}
            </button>)}
        </div>
      </div>
    </div>;
}